import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { services, cities } from '../mock';
import { Search, MapPin, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const ServiceSearch = () => {
  const navigate = useNavigate();
  const [selectedService, setSelectedService] = useState('');
  const [cityInput, setCityInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredCities, setFilteredCities] = useState([]);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Filter cities based on input
  useEffect(() => {
    if (cityInput.length > 0) {
      const filtered = cities.filter(city => 
        city.toLowerCase().includes(cityInput.toLowerCase())
      ).slice(0, 8); // Limit to 8 suggestions
      setFilteredCities(filtered);
      setShowSuggestions(true);
    } else {
      setFilteredCities([]);
      setShowSuggestions(false);
    }
  }, [cityInput]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCitySelect = (city) => {
    setCityInput(city);
    setShowSuggestions(false);
  };

  const handleSearch = () => {
    if (selectedService) {
      navigate(`/devis-premium?service=${selectedService}`);
    } else {
      navigate('/devis-premium');
    }
  };

  return (
    <section className="bg-[#F4F7F6] py-16">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8 text-[#153254]">
            De quoi avez-vous besoin ?
          </h2>
          
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
            <div className="grid md:grid-cols-3 gap-6">
              {/* Service Selection */}
              <div className="md:col-span-1">
                <label className="block text-sm font-medium mb-2 text-[#153254]">Service</label>
                <Select value={selectedService} onValueChange={setSelectedService}>
                  <SelectTrigger className="bg-white border-gray-300 text-[#153254] h-12">
                    <SelectValue placeholder="Sélectionner un service" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {services.map(service => (
                      <SelectItem key={service.id} value={service.id}>
                        {service.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* City Input with Autocomplete */}
              <div className="md:col-span-1 relative">
                <label className="block text-sm font-medium mb-2 text-[#153254]">Ville ou Code postal</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    ref={inputRef}
                    type="text"
                    placeholder="Tapez votre ville..."
                    value={cityInput}
                    onChange={(e) => setCityInput(e.target.value)}
                    onFocus={() => cityInput.length > 0 && setShowSuggestions(true)}
                    className="pl-10 pr-10 h-12 border-gray-300"
                  />
                  {cityInput && (
                    <button
                      onClick={() => setCityInput('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                
                {/* Autocomplete Suggestions */}
                {showSuggestions && filteredCities.length > 0 && (
                  <div 
                    ref={suggestionsRef}
                    className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl max-h-64 overflow-y-auto"
                  >
                    {filteredCities.map((city, index) => (
                      <button
                        key={index}
                        onClick={() => handleCitySelect(city)}
                        className="w-full px-4 py-3 text-left hover:bg-[#122b26]/5 flex items-center gap-3 transition-colors border-b border-gray-100 last:border-0"
                      >
                        <MapPin className="h-4 w-4 text-[#122b26]" />
                        <span className="text-[#153254]">{city}</span>
                      </button>
                    ))}
                  </div>
                )}
                
                {/* No results message */}
                {showSuggestions && cityInput.length > 2 && filteredCities.length === 0 && (
                  <div 
                    ref={suggestionsRef}
                    className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl p-4"
                  >
                    <p className="text-gray-500 text-sm text-center">
                      Aucune ville trouvée. Contactez-nous pour vérifier notre zone d'intervention.
                    </p>
                  </div>
                )}
              </div>

              {/* Search Button */}
              <div className="flex items-end">
                <Button 
                  onClick={handleSearch} 
                  className="w-full bg-[#122b26] hover:bg-[#0e2320] text-white font-semibold rounded-lg h-12"
                  size="lg"
                >
                  <Search className="mr-2 h-5 w-5" />
                  Chercher
                </Button>
              </div>
            </div>

            <p className="text-center text-sm text-gray-500 mt-6">
              Nous intervenons dans un rayon de <span className="font-semibold text-[#122b26]">50 km</span> autour de Méru
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
